import routes from './routes';

type MemberKeyProps = { memberKey: string };
type DataownerKeyProps = { dataownerKey: string };
type LocationKeyProps = { locationKey: string };
type EmployeeIdProps = { employeeId: string };
type EventIdProps = { eventId: string };
type ModuleProps = { moduleSlug: string };
type ModuleCategoryProps = { categorySlug: string; moduleSlug: string };
type SubjectProps = { subject: string };
type SubjectContentProps = { subject: string; slug: string };
type SocialFeedProps = { slug: string };

export const getDashboardRoute = () => `/${routes.home}`;

export const getSettingsRoute = () => `/${routes.settings}`;

// Memberadmin routes

export const getMemberadminRoute = () => `/${routes.memberadmin}`;

export const getBrandDetailsRoute = () => `${getMemberadminRoute()}/${routes.brandDetails}`;
export const getBrandDetailsConfigurationsRoute = () =>
  `${getBrandDetailsRoute()}/${routes.configurations}`;
export const getBrandDetailsAdministratorsRoute = () =>
  `${getBrandDetailsRoute()}/${routes.administrators}`;
export const getBrandPropertiesRoute = () => `${getBrandDetailsRoute()}/${routes.properties}`;
export const getBrandDetailsChangelogRoute = () => `${getBrandDetailsRoute()}/${routes.changelog}`;

export const getMemberRoute = ({ memberKey }: MemberKeyProps) =>
  `${getMemberadminRoute()}/${routes.member}/${memberKey}`;
export const getMemberLocationsRoute = ({ memberKey }: MemberKeyProps) =>
  `${getMemberadminRoute()}/${routes.member}/${memberKey}/${routes.locations}`;
export const getMemberEmployeesRoute = ({ memberKey }: MemberKeyProps) =>
  `${getMemberadminRoute()}/${routes.member}/${memberKey}/${routes.employees}`;
export const getMemberPropertiesRoute = ({ memberKey }: MemberKeyProps) =>
  `${getMemberadminRoute()}/${routes.member}/${memberKey}/${routes.properties}`;

export const getEmployeeRoute = ({ employeeId }: EmployeeIdProps) =>
  `${getMemberadminRoute()}/${routes.employee}/${employeeId}`;

// Content routes

export const getAllEventsRoute = () => `/${routes.events}`;

export const getEventRoute = ({ eventId }: EventIdProps) => `${getAllEventsRoute()}/${eventId}`;

export const getAllNewsRoute = () => `/${routes.news}`;

export const getAllServicesRoute = () => `/${routes.services}`;

export const getModuleRoute = ({ moduleSlug }: ModuleProps) => `/${routes.module}/${moduleSlug}`;

export const getMosaicModuleRoute = ({ moduleSlug }: ModuleProps) =>
  `/${routes.module}/${routes.mosaic}/${moduleSlug}`;

export const getModuleCategoryRoute = ({ categorySlug, moduleSlug }: ModuleCategoryProps) =>
  `${getModuleRoute({ moduleSlug: moduleSlug })}/${categorySlug}`;

export const getOrderRoute = ({ moduleSlug }: ModuleProps) =>
  `/${routes.module}/${routes.order}/${moduleSlug}`;
export const getOrderCategoryRoute = ({ categorySlug, moduleSlug }: ModuleCategoryProps) =>
  `${getOrderRoute({ moduleSlug })}/${categorySlug}`;
export const getOrderSummaryRoute = ({ moduleSlug }: ModuleProps) =>
  `${getOrderRoute({ moduleSlug })}/${routes.orderSummary}`;
export const getContactUsRoute = ({ moduleSlug }: ModuleProps) =>
  `${getOrderRoute({ moduleSlug })}/${routes.contact}`;

export const getTopicPageRoute = (topicSlug: string) => `/${routes.topic}/${topicSlug}`;

// Subject routes

export const getSubjectIndexRoute = ({ subject }: SubjectProps) => `/${routes.subject}/${subject}`;

export const getSubjectTopicPageRoute = ({ subject, slug }: SubjectContentProps) =>
  `/${routes.subject}/${subject}/${routes.topic}/${slug}`;

export const getSubjectArticleIndexRoute = ({ subject }: SubjectProps) =>
  `/${routes.subject}/${subject}/${routes.articles}`;

export const getSubjectArticlePageRoute = ({ subject, slug }: SubjectContentProps) =>
  `/${routes.subject}/${subject}/${routes.articles}/${slug}`;

export const getSubjectEventIndexRoute = ({ subject }: SubjectProps) =>
  `/${routes.subject}/${subject}/${routes.calendar}`;

export const getSubjectEventPageRoute = ({ subject, slug }: SubjectContentProps) =>
  `/${routes.subject}/${subject}/${routes.calendar}/${slug}`;

// Social feed routes

type SocialFeedPostRouteProps = { postId: string; commentKey?: string };
type SlugSocialFeedPostRouteProps = SocialFeedProps & SocialFeedPostRouteProps;

const withPostQuery = (path: string, postId: string, commentKey?: string) => {
  const params = new URLSearchParams({ post: postId });
  if (commentKey) {
    params.set('comment', commentKey);
  }
  return `${path}?${params.toString()}`;
};

export const getSocialFeedsRoute = () => `/${routes.socialFeed}`;
export const getSocialFeedRoute = ({ slug }: SocialFeedProps) => `/${routes.socialFeed}/${slug}`;
export const getCommonSocialFeedRoute = ({ slug }: SocialFeedProps) =>
  `/${routes.socialFeed}/${routes.commonSocialFeed}/${slug}`;

// These build a standalone "cold" URL to a post - the feed's own page with `?post=`/`&comment=`
// already set, so the post modal opens as soon as the page mounts. Not currently called from
// within the app itself (in-app navigation opens the modal via `usePostModalParams` on
// whatever page is already showing, so it never has to leave that page) - kept for a
// share/notification link to a specific post.
export const getSocialFeedsIndexPostRoute = ({ postId, commentKey }: SocialFeedPostRouteProps) =>
  withPostQuery(getSocialFeedsRoute(), postId, commentKey);
export const getSocialFeedPostRoute = ({
  slug,
  postId,
  commentKey,
}: SlugSocialFeedPostRouteProps) => withPostQuery(getSocialFeedRoute({ slug }), postId, commentKey);
export const getCommonSocialFeedPostRoute = ({
  slug,
  postId,
  commentKey,
}: SlugSocialFeedPostRouteProps) =>
  withPostQuery(getCommonSocialFeedRoute({ slug }), postId, commentKey);

// Notification unsubscribe links
//
// Linked from notification emails. Handled on any authenticated page, so the links land on the
// dashboard: the target/feed is unsubscribed, a message is shown and the params are removed.

export const unsubscribeTargetParam = 'unsubscribeTarget';
export const unsubscribeFeedParam = 'unsubscribeFeed';

export const getUnsubscribeTargetRoute = (targetId: string) =>
  `${getDashboardRoute()}?${new URLSearchParams({ [unsubscribeTargetParam]: targetId })}`;
// A socialFeed or commonSocialFeed id
export const getUnsubscribeFeedRoute = (feedId: string) =>
  `${getDashboardRoute()}?${new URLSearchParams({ [unsubscribeFeedParam]: feedId })}`;

// Form routes

export const getFormsRoute = () => `/${routes.form}`;
export const getFormRoute = ({ slug }: { slug: string }) => `/${routes.form}/${slug}`;
